import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { MailIcon, PaperclipIcon, Loader2Icon, InfoIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Mali Müşavire Belge Gönderme Modalı — filtre uygulanmış belgeler
// ZIP olarak paketlenip muhasebeci e-postasına gönderilir. CC opsiyonel,
// muhasebeci adresi otomatik kaydedilebilir.
const SoloDocumentsSendModal = ({ open, filterState, filteredCount, onClose }) => {
  const [recipient, setRecipient] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [ccSelf, setCcSelf] = useState(true);
  const [saveAccountant, setSaveAccountant] = useState(true);
  const [sending, setSending] = useState(false);

  // Modal açılırken kayıtlı muhasebeci e-postasını çek
  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('access_token');
    axios.get(`${BACKEND_URL}/api/users/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        const em = r.data?.accountant_email || '';
        setRecipient(em);
      })
      .catch(() => { /* sessizce yut */ });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!recipient || !recipient.includes('@')) {
      toast.error('Geçerli bir muhasebeci e-postası girin');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('access_token');
      const cc_emails = ccEmail && ccEmail.includes('@') ? [ccEmail.trim()] : [];
      const resp = await axios.post(`${BACKEND_URL}/api/solo/documents/send`, {
        year: filterState.yearFilter,
        month: filterState.monthFilter,
        day: filterState.dayFilter,
        category: filterState.categoryFilter,
        expense_type: filterState.typeFilter,
        search: filterState.search,
        recipient_email: recipient.trim(),
        cc_emails,
        cc_self: ccSelf,
        save_accountant: saveAccountant,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const d = resp.data || {};
      if (d.mocked) {
        toast.success(`Simüle edildi: ${d.included} belge, ${d.filename} (SendGrid yapılandırılmamış)`);
      } else {
        toast.success(`${d.included} belge gönderildi — ${d.filename}`);
      }
      if (d.failed && d.failed.length > 0) {
        toast.warning(`${d.failed.length} belge indirilemedi (ZIP'e eklenmedi).`);
      }
      onClose && onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Gönderim başarısız';
      toast.error(typeof msg === 'string' ? msg : 'Gönderim başarısız');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !sending) onClose && onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="solo-docs-send-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
              <MailIcon className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <DialogTitle>Mali Müşavire Gönder</DialogTitle>
              <p className="text-xs text-gray-500 mt-0.5">
                Filtreye uyan belgeler ZIP olarak e-posta ile gönderilecek
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="my-3 p-3 rounded-lg bg-teal-50 border border-teal-200 flex items-center gap-2 text-sm text-teal-800">
          <PaperclipIcon className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong data-testid="solo-docs-send-count">{filteredCount}</strong> belge tek ZIP dosyasında paketlenip ekli olarak gönderilir.
          </span>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Muhasebeci E-postası *</label>
            <Input
              type="email"
              value={recipient}
              onChange={e => setRecipient(e.target.value)}
              placeholder="muhasebeci@example.com"
              required
              autoFocus
              data-testid="solo-docs-send-recipient"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CC E-postası (opsiyonel)</label>
            <Input
              type="email"
              value={ccEmail}
              onChange={e => setCcEmail(e.target.value)}
              placeholder="ekstra@example.com — boş bırakılabilir"
              data-testid="solo-docs-send-cc"
            />
          </div>

          <div className="space-y-2 pt-1">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={ccSelf}
                onChange={e => setCcSelf(e.target.checked)}
                className="w-4 h-4 rounded text-teal-600"
                data-testid="solo-docs-send-cc-self"
              />
              Bana da bir kopyasını gönder
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={saveAccountant}
                onChange={e => setSaveAccountant(e.target.checked)}
                className="w-4 h-4 rounded text-teal-600"
                data-testid="solo-docs-send-save"
              />
              Bu adresi kaydet (bir dahaki sefere otomatik gelsin)
            </label>
          </div>

          <div className="flex items-start gap-2 text-xs text-gray-500 pt-2">
            <InfoIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              Belgeler ZIP içinde <strong>Yıl / Ay</strong> klasörlerine göre otomatik düzenlenir (örn. <code>2026/01-Ocak/</code>). Her klasörde <code>00_LISTE.txt</code>, <code>00_LISTE.xlsx</code> (filtre/formül destekli) ve <code>00_LISTE.pdf</code> (baskıya hazır) yer alır. Kökte <code>00_MANIFEST.txt</code> tüm klasör özetini içerir.
            </span>
          </div>

          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={sending}>
              İptal
            </Button>
            <Button
              type="submit"
              disabled={sending || filteredCount === 0}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="solo-docs-send-submit"
            >
              {sending ? (
                <><Loader2Icon className="w-4 h-4 animate-spin mr-1.5" /> Gönderiliyor...</>
              ) : (
                <><MailIcon className="w-4 h-4 mr-1.5" /> Gönder</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SoloDocumentsSendModal;
